import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { trackButtonClick } from "@/lib/pixel";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // Apple-style Blue System
        "hero-blue-ring": "relative inline-flex items-center justify-center px-10 py-4 bg-white text-[#0a0a0a] font-medium text-lg tracking-tight rounded-xl transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_0_40px_rgba(0,113,227,0.6)] overflow-visible",
        "hero-black-ring": "relative inline-flex items-center justify-center px-10 py-4 bg-[#0a0a0a] text-white font-medium text-lg tracking-tight rounded-xl transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_0_40px_rgba(0,113,227,0.6)] overflow-visible",
        "hero-blue-solid": "bg-[#0071e3] text-white font-semibold rounded-xl px-8 py-4 text-base transition-all duration-[600ms] ease-[cubic-bezier(0.16,1,0.3,1)] shadow-[0_2px_8px_rgba(0,113,227,0.3),0_1px_3px_rgba(0,113,227,0.15)] hover:shadow-[0_6px_16px_rgba(0,113,227,0.4),0_2px_4px_rgba(0,113,227,0.2)] hover:translate-y-[-2px] hover:brightness-110 active:scale-[0.98]",
        "hero-outline": "bg-transparent border-2 border-white/30 text-white font-semibold rounded-xl px-8 py-4 text-base hover:bg-[#0a0a0a]/60 hover:border-white/50 hover:shadow-lg hover:shadow-white/10 transition-all duration-300",
        "secondary-link": "text-white/60 hover:text-white font-medium underline-offset-4 hover:underline transition-colors duration-200",
        nav: "text-white/60 hover:text-white transition-colors duration-200 text-sm font-medium relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-gradient-accent after:scale-x-0 after:origin-left after:transition-transform after:duration-300 hover:after:scale-x-100",
        "nav-blue": "bg-[#0071e3] text-white font-semibold rounded-lg px-4 py-2 text-sm transition-all duration-300 ease-out hover:bg-white hover:text-[#0071e3] hover:scale-[1.05] hover:shadow-[0_4px_16px_rgba(0,113,227,0.5)] active:scale-[0.98]",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        hero: "h-14 px-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  trackingName?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, trackingName, onClick, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      // Track button click with name from trackingName prop, aria-label, or children text
      const buttonText = trackingName || 
        props['aria-label'] || 
        (typeof props.children === 'string' ? props.children : 'button');
      trackButtonClick(buttonText);
      
      // Call original onClick if provided
      onClick?.(e);
    };
    
    return (
      <Comp 
        className={cn(buttonVariants({ variant, size, className }))} 
        ref={ref} 
        onClick={handleClick}
        {...props} 
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
